import { createContext, useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { I18nextProvider } from 'react-i18next';

import useErrorPopup from '../tools/hooks/showError';
import useLanguagePreferences from '../components/settings/hooks/useLanguagePreferences';
import i18n, { LANGUAGE_NAME_TO_CODE } from '../i18n';

const defaultHandleLanguageChange = () => {};
const defaultSaveLanguagePreference = async () => true;
const defaultGetLanguagePreference = async () => 'English';

const LANGUAGE_PREFS_FALLBACK = {
  language: 'English',
  isLoading: false,
  handleLanguageChange: defaultHandleLanguageChange,
  saveLanguagePreference: defaultSaveLanguagePreference,
  getLanguagePreference: defaultGetLanguagePreference,
};

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const LanguageProvider = ({ children }) => {
  const [showError, ErrorPopupComponent] = useErrorPopup();
  const languagePreferences = useLanguagePreferences(showError) || {};
  const {
    language = LANGUAGE_PREFS_FALLBACK.language,
    isLoading = LANGUAGE_PREFS_FALLBACK.isLoading,
    handleLanguageChange = LANGUAGE_PREFS_FALLBACK.handleLanguageChange,
    saveLanguagePreference = LANGUAGE_PREFS_FALLBACK.saveLanguagePreference,
    getLanguagePreference = LANGUAGE_PREFS_FALLBACK.getLanguagePreference,
  } = languagePreferences;

  const languageCode = LANGUAGE_NAME_TO_CODE[language] || 'en';
  const dir = languageCode === 'he' ? 'rtl' : 'ltr';
  useEffect(() => {
    if (i18n.language !== languageCode) {
      i18n.changeLanguage(languageCode);
    }
  }, [languageCode]);

  const value = useMemo(
    () => ({
      language,
      dir,
      languageCode,
      isLoading,
      handleLanguageChange,
      saveLanguagePreference,
      getLanguagePreference,
      showError,
      ErrorPopupComponent,
    }),
    [
      language,
      dir,
      languageCode,
      isLoading,
      handleLanguageChange,
      saveLanguagePreference,
      getLanguagePreference,
      showError,
      ErrorPopupComponent,
    ],
  );

  return (
    <LanguageContext.Provider value={value}>
      <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
    </LanguageContext.Provider>
  );
};

LanguageProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default LanguageProvider;

